<script setup lang="ts">
import { computed } from 'vue'
import { useToast } from '#imports'

/**
 * Floating "Dismiss all" pill mounted once in app.vue, visible whenever
 * more than one toast is on screen. Clears the entire toast stack via
 * Nuxt UI's `useToast().clear()`.
 *
 * Teleported to <body> so it escapes any stacking context created by
 * <UApp> / <UToaster> / reka-ui's <ToastProvider> — Firefox in particular
 * is strict about `position: fixed` being relative to the nearest
 * transformed/filtered/contained ancestor. z-index is set above Nuxt UI's
 * toast viewport so the pill always sits on top.
 */
const { toasts, clear } = useToast()

const visibleCount = computed(() => toasts.value.length)
const show = computed(() => visibleCount.value > 1)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <button
        v-if="show"
        type="button"
        class="dismiss-all-btn"
        :aria-label="`Dismiss all ${visibleCount} toasts`"
        @click="clear"
      >
        <UIcon name="ic:outline-close" class="h-4 w-4" />
        Dismiss All ({{ visibleCount }})
      </button>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Inline styles instead of Tailwind arbitrary-value classes — guarantees
   the button renders identically even if the page-scoped style cascade or
   a CSS framework purge step would otherwise strip a `z-[10000]`-style
   class. */
.dismiss-all-btn {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 10000;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border-radius: 0;
  border: 1px solid var(--color-border-primary, #4b5563);
  background: var(--color-background-primary, #1f2937);
  color: var(--color-foreground-primary, #f3f4f6);
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, transform 120ms ease;
}
.dismiss-all-btn:hover {
  background: var(--color-background-secondary, #111827);
  transform: translateY(-1px);
}
.dismiss-all-btn:active {
  transform: translateY(0);
}
</style>
